const strip = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ');
const key = () => `planning-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
const asText = (value) => {
  if (value == null) return '';
  if (value instanceof Date) return value;
  if (typeof value === 'object') {
    if ('result' in value && value.result != null) return value.result;
    if ('text' in value && value.text != null) return value.text;
    if (Array.isArray(value.richText)) return value.richText.map((part) => part.text || '').join('');
  }
  return value;
};
const dateOnly = (value) => {
  value = asText(value);
  if (!value) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString().slice(0, 10);
  if (typeof value === 'number' && value > 20000 && value < 100000) {
    const excelEpoch = Date.UTC(1899, 11, 30);
    return new Date(excelEpoch + Math.round(value) * 86400000).toISOString().slice(0, 10);
  }
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const match = raw.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (match) {
    const year = Number(match[3]) < 100 ? 2000 + Number(match[3]) : Number(match[3]);
    return `${year}-${String(match[2]).padStart(2, '0')}-${String(match[1]).padStart(2, '0')}`;
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
};
const number = (value) => {
  const normalized = String(asText(value) ?? '').replace(/\s/g, '').replace(',', '.').replace(/[^0-9.+-]/g, '');
  if (!normalized) return '';
  const result = Number(normalized);
  return Number.isFinite(result) && result >= 0 ? result : '';
};
const ITEM_ALIASES = {
  type: ['type', 'nature', 'type element', 'type d element'],
  title: ['titre', 'designation', 'element', 'tache', 'livrable', 'jalon', 'nom'],
  phase: ['phase', 'phase code'],
  lot: ['lot', 'corps d etat', 'discipline'],
  hours: ['temps', 'heures', 'charge', 'charge h', 'temps h', 'duree h'],
  start: ['debut', 'date debut', 'demarrage', 'date demarrage'],
  end: ['fin', 'date fin', 'echeance', 'livraison', 'date livraison', 'jalon date'],
  dependency: ['depend de', 'dependance', 'predecesseur', 'predecesseurs'],
  notes: ['notes', 'commentaire', 'commentaires', 'remarque'],
};
const indexHeaders = (headers) => {
  const normalized = headers.map((header) => strip(asText(header)));
  const indexes = {};
  Object.entries(ITEM_ALIASES).forEach(([field, aliases]) => {
    indexes[field] = normalized.findIndex((header) => aliases.includes(header));
  });
  return indexes;
};
const read = (row, index) => index >= 0 ? asText(row[index]) : '';
const typeOf = (value, title) => {
  const normalized = strip(value);
  if (normalized.includes('jalon') || normalized.includes('milestone')) return 'milestone';
  if (normalized.includes('phase')) return 'phase';
  if (!normalized && /\bjalon\b/i.test(String(title || ''))) return 'milestone';
  return 'work';
};
export function planningRowsToItems(rows) {
  if (!Array.isArray(rows) || rows.length < 2) throw new Error('Le fichier ne contient aucune donnée exploitable.');
  const indexes = indexHeaders(rows[0]);
  if (indexes.title < 0) throw new Error('Colonne « Titre », « Désignation », « Élément » ou « Livrable » introuvable.');
  if (indexes.start < 0 && indexes.end < 0) throw new Error('Aucune colonne de date (« Début », « Fin », « Échéance » ou « Livraison ») n’a été trouvée.');
  const pendingDependencies = [];
  const items = rows.slice(1).map((row) => {
    const title = String(read(row, indexes.title) || '').trim();
    if (!title) return null;
    const itemType = typeOf(read(row, indexes.type), title);
    let startDate = dateOnly(read(row, indexes.start));
    let endDate = dateOnly(read(row, indexes.end));
    if (itemType === 'milestone') { startDate = startDate || endDate; endDate = endDate || startDate; }
    const item = {
      clientKey: key(), itemType, source: 'client', title,
      phaseCode: String(read(row, indexes.phase) || '').trim().toUpperCase(),
      lotCode: String(read(row, indexes.lot) || '').trim().toUpperCase(),
      documentTypeCode: '', startDate, endDate,
      estimatedHours: itemType === 'work' ? number(read(row, indexes.hours)) : '',
      dependencyIds: [], clientConstraintKey: null,
      isClientConstraint: true, isLocked: true,
      notes: String(read(row, indexes.notes) || '').trim(),
    };
    pendingDependencies.push({ clientKey: item.clientKey, raw: String(read(row, indexes.dependency) || '').trim() });
    return item;
  }).filter(Boolean);
  const titleMap = new Map(items.map((item) => [strip(item.title), item.clientKey]));
  pendingDependencies.forEach(({ clientKey, raw }) => {
    const item = items.find((candidate) => candidate.clientKey === clientKey);
    if (!item || !raw) return;
    item.dependencyIds = raw.split(/[;,|]/).map((value) => titleMap.get(strip(value))).filter(Boolean);
  });
  if (!items.length) throw new Error('Aucune ligne de planning exploitable n’a été trouvée.');
  return items;
}
const parseDelimitedLine = (line, delimiter) => {
  const values = []; let value = ''; let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (quoted && line[i + 1] === '"') { value += '"'; i += 1; }
      else quoted = !quoted;
    } else if (char === delimiter && !quoted) { values.push(value); value = ''; }
    else value += char;
  }
  values.push(value); return values;
};
export function parsePlanningCsv(text) {
  const lines = String(text || '').replace(/^\uFEFF/, '').split(/\r?\n/).filter((line) => line.trim());
  if (!lines.length) throw new Error('Le fichier CSV est vide.');
  const candidates = [';', '\t', ','];
  const delimiter = candidates.sort((a, b) => lines[0].split(b).length - lines[0].split(a).length)[0];
  return planningRowsToItems(lines.map((line) => parseDelimitedLine(line, delimiter)));
}
export async function parsePlanningFile(file) {
  const name = String(file?.name || '').toLowerCase();
  if (name.endsWith('.csv') || name.endsWith('.txt')) return parsePlanningCsv(await file.text());
  if (name.endsWith('.xlsx')) {
    const module = await import('exceljs'); const ExcelJS = module.default || module;
    const workbook = new ExcelJS.Workbook(); await workbook.xlsx.load(await file.arrayBuffer());
    const sheet = workbook.worksheets[0];
    if (!sheet) throw new Error('Le classeur Excel ne contient aucune feuille.');
    const rows = [];
    sheet.eachRow({ includeEmpty: false }, (row) => rows.push(row.values.slice(1)));
    return planningRowsToItems(rows);
  }
  throw new Error('Format non pris en charge. Utilisez un fichier .xlsx ou .csv.');
}
